import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  PencilIcon, 
  TrashIcon, 
  PlusIcon,
  PowerIcon
} from '@heroicons/react/24/outline';

export default function Faqs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    active: true
  });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/faq/', { withCredentials: true });
      setFaqs(response.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch FAQs:', err);
      setError('Failed to load FAQs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const resetForm = () => {
    setFormData({
      question: '',
      answer: '',
      active: true
    });
    setEditingFaq(null);
  };

  const startEditing = (faq) => {
    setEditingFaq(faq.id);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      active: faq.active
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFaq) {
        // Update existing FAQ
        await axios.post('/api/admin/faq/edit', {
          ...formData,
          id: editingFaq
        }, { withCredentials: true });
      } else {
        // Create new FAQ
        await axios.post('/api/admin/faq/create', formData, { withCredentials: true });
      }
      resetForm();
      fetchFaqs(); // Refresh the list
    } catch (err) {
      console.error(`Failed to ${editingFaq ? 'update' : 'create'} FAQ:`, err);
      setError(`Failed to ${editingFaq ? 'update' : 'create'} FAQ. Please try again.`);
    }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    
    try {
      await axios.post('/api/admin/faq/delete', { id }, { withCredentials: true });
      fetchFaqs(); // Refresh the list
    } catch (err) {
      console.error('Failed to delete FAQ:', err);
      setError('Failed to delete FAQ. Please try again.');
    }
  };

  const toggleActive = async (faq) => {
    try {
      await axios.post('/api/admin/faq/edit', {
        ...faq,
        active: !faq.active
      }, { withCredentials: true });
      fetchFaqs(); // Refresh the list
    } catch (err) {
      console.error('Failed to update FAQ status:', err);
      setError('Failed to update FAQ status. Please try again.');
    }
  };

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">FAQ Management</h2>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">FAQ Management</h2>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-md p-4 mb-6">
        <h3 className="text-lg font-semibold mb-4">{editingFaq ? 'Edit FAQ' : 'Add New FAQ'}</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
            <input
              type="text"
              name="question"
              value={formData.question}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
            <textarea
              name="answer"
              value={formData.answer}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows="4"
              required
            ></textarea>
          </div>

          <div>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div className="flex justify-end space-x-2">
            {editingFaq && (
              <button
                type="button"
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md"
                onClick={resetForm}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md"
            >
              {editingFaq ? 'Update FAQ' : 'Add FAQ'}
            </button>
          </div>
        </div>
      </form>

      <div className="mb-6">
        <input
          type="text"
          placeholder="Search FAQs..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="space-y-4">
        {filteredFaqs.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            No FAQs found.
          </div>
        ) : (
          filteredFaqs.map((faq) => (
            <div key={faq.id} className="bg-white shadow-md rounded-md p-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className={`font-semibold ${!faq.active ? 'text-gray-400' : 'text-gray-900'}`}>{faq.question}</h4>
                <div className="flex space-x-2">
                  <button
                    onClick={() => startEditing(faq)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md"
                    title="Edit FAQ"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => toggleActive(faq)}
                    className={`text-white px-3 py-1 rounded-md ${
                      faq.active
                        ? 'bg-yellow-500 hover:bg-yellow-600'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                    title={faq.active ? 'Deactivate FAQ' : 'Activate FAQ'}
                  >
                    <PowerIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteFaq(faq.id)}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md"
                    title="Delete FAQ"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className={`text-sm ${!faq.active ? 'text-gray-400' : 'text-gray-600'}`}>
                {faq.answer}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
